import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { motion, AnimatePresence } from 'framer-motion';

interface SunscapesCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedProduct?: string | null;
}

const steps = [
  'Tickets',
  'Lodging',
  'Summary',
];

// Ticket options and prices
const TICKET_OPTIONS = [
  {
    key: 'half',
    label: 'Half Experience',
    description: 'Thursday evening, Friday daytime, Sunday full day',
    price: 300,
  },
  {
    key: 'full',
    label: 'Full Experience',
    description: 'All activities across the 4 days',
    price: 550,
  },
  {
    key: 'package',
    label: 'Before Sunset Package',
    description: 'Includes lodging for 4 nights and 2 full experience tickets',
    price: 3200,
  },
];

// Lodging options and prices
const LODGING_OPTIONS = [
  {
    key: 'single',
    label: 'Radisson Blu Single Room',
    description: '1 person, 4 nights, breakfast included',
    price: 900,
    perNight: 225,
  },
  {
    key: 'double',
    label: 'Radisson Blu Double Room',
    description: '2 people, 4 nights, breakfast included',
    price: 950,
    perNight: 240,
  },
];

const SunscapesCheckoutModal: React.FC<SunscapesCheckoutModalProps> = ({ isOpen, onClose, preselectedProduct }) => {
  const [currentStep, setCurrentStep] = useState(0);
  // Ticket quantities
  const [ticketQuantities, setTicketQuantities] = useState({ half: 0, full: 0, package: 0 });
  // Lodging quantities
  const [lodgingQuantities, setLodgingQuantities] = useState({ single: 0, double: 0 });
  const [isLoading, setIsLoading] = useState(false);
  // Preselect product on first open
  React.useEffect(() => {
    if (!isOpen || !preselectedProduct) return;
    if (['half', 'full', 'package'].includes(preselectedProduct)) {
      setTicketQuantities(q => ({ ...q, [preselectedProduct]: 1 }));
      setCurrentStep(0);
    } else if (['single', 'double'].includes(preselectedProduct)) {
      setLodgingQuantities(q => ({ ...q, [preselectedProduct]: 1 }));
      setCurrentStep(1);
    }
    // Only preselect on first open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselectedProduct]);

  // Placeholder price IDs and experience ID
  const EXPERIENCE_ID = '9611812e-270f-4379-8cc9-801ccce37139';
  const PRICE_IDS = {
    half: 'price_1RPF6iI8q53ewpK1qeCubnG6', // Half Experience
    full: 'price_1RPF6jI8q53ewpK1OphnpZz6', // Full Experience
    package: 'price_1RPF6jI8q53ewpK1h238bUgl', // Before Sunset Package
    single: 'price_1RPF6kI8q53ewpK10Gtykhoe', // Radisson Blu Single Room
    double: 'price_1RPF6kI8q53ewpK1pH7g0pJx', // Radisson Blu Double Room
  };

  // Calculate subtotals
  const ticketSubtotal = TICKET_OPTIONS.reduce(
    (sum, opt) => sum + ticketQuantities[opt.key as keyof typeof ticketQuantities] * opt.price,
    0
  );
  const lodgingSubtotal = LODGING_OPTIONS.reduce(
    (sum, opt) => sum + lodgingQuantities[opt.key as keyof typeof lodgingQuantities] * opt.price,
    0
  );
  const grandTotal = ticketSubtotal + lodgingSubtotal;
  const processingFee = Math.round(grandTotal * 0.06);
  const totalWithFee = grandTotal + processingFee;

  // Stripe checkout handler (multi-line)
  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      // Gather all selected tickets and lodging
      // --- Ticket vs Lodging categorization ---
      const ticketLineItems = TICKET_OPTIONS
        .filter(opt => ticketQuantities[opt.key as keyof typeof ticketQuantities] > 0)
        .map(opt => ({
          price: PRICE_IDS[opt.key as keyof typeof PRICE_IDS],
          quantity: ticketQuantities[opt.key as keyof typeof ticketQuantities],
          total: ticketQuantities[opt.key as keyof typeof ticketQuantities] * opt.price,
          type: 'ticket',
        }));
      const lodgingLineItems = LODGING_OPTIONS
        .filter(opt => lodgingQuantities[opt.key as keyof typeof lodgingQuantities] > 0)
        .map(opt => ({
          price: PRICE_IDS[opt.key as keyof typeof PRICE_IDS],
          quantity: lodgingQuantities[opt.key as keyof typeof lodgingQuantities],
          total: lodgingQuantities[opt.key as keyof typeof lodgingQuantities] * opt.price,
          type: 'lodging',
        }));
      const allLineItems = [...ticketLineItems, ...lodgingLineItems];
      if (allLineItems.length === 0) {
        setIsLoading(false);
        return;
      }
      // Calculate total and add processing fee
      const total = allLineItems.reduce((sum, item) => sum + item.total, 0);
      const totalWithFee = Math.round(total * 1.06);
      // Calculate participant_count as the sum of ticket quantities
      const participantCount = ticketLineItems.reduce((sum, item) => sum + item.quantity, 0);
      // Call backend with all line items and correct participant count
      await createBookingAndRedirectToStripe(
        EXPERIENCE_ID,
        allLineItems,
        participantCount,
        totalWithFee
      );
    } catch (error) {
      setIsLoading(false);
      alert('There was an error starting checkout. Please try again.');
    }
  };

  // Prevent checkout if nothing is selected
  const canCheckout = ticketSubtotal > 0 || lodgingSubtotal > 0;

  if (!isOpen) return null;

  // Match Xamanic modal container and panel
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center items-end sm:items-center sm:p-8">
      <div className="relative w-full max-w-md bg-earth-900 border border-earth-700 shadow-2xl rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 text-sand-100">
        <button
          className="absolute top-4 right-4 text-sand-400 hover:text-sand-200"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
        {/* Stepper */}
        <div className="flex justify-center gap-2 mt-2 mb-8">
          {steps.map((step, idx) => (
            <div
              key={step}
              className={`flex-1 px-2 py-1 rounded-full text-center text-xs font-medium transition-all ${
                idx === currentStep
                  ? 'bg-sand-200 text-earth-900' : 'bg-earth-800 text-sand-400'
              }`}
            >
              {step}
            </div>
          ))}
        </div>
        {/* Step Content */}
        <div className="flex-1 px-2 pb-8 pt-2 flex flex-col justify-between">
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-display mb-6">Select Tickets</h2>
              <div className="space-y-4">
                {TICKET_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-earth-800/70 rounded-lg p-4 gap-2">
                    <div>
                      <div className="font-semibold text-sand-100">{opt.label}</div>
                      <div className="text-sand-300 text-xs mb-1">{opt.description}</div>
                      <div className="text-sand-200 font-medium">€{opt.price}</div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                      <button
                        className="w-8 h-8 flex items-center justify-center rounded bg-earth-700 text-sand-200 text-lg font-bold disabled:opacity-50"
                        onClick={() => setTicketQuantities(q => ({ ...q, [opt.key]: Math.max(0, q[opt.key as keyof typeof q] - 1) }))}
                        disabled={ticketQuantities[opt.key as keyof typeof ticketQuantities] === 0}
                        aria-label={`Remove one ${opt.label}`}
                      >
                        -
                      </button>
                      <span className="w-6 text-center text-sand-100 font-semibold">{ticketQuantities[opt.key as keyof typeof ticketQuantities]}</span>
                      <button
                        className="w-8 h-8 flex items-center justify-center rounded bg-sand-200 text-earth-900 text-lg font-bold"
                        onClick={() => setTicketQuantities(q => ({ ...q, [opt.key]: q[opt.key as keyof typeof q] + 1 }))}
                        aria-label={`Add one ${opt.label}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-6 border-t border-earth-700 pt-4">
                <span className="font-semibold text-sand-200">Tickets Subtotal</span>
                <span className="text-lg font-bold text-sand-100">€{ticketSubtotal}</span>
              </div>
            </div>
          )}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-display mb-6">Add Lodging</h2>
              <div className="space-y-4">
                {LODGING_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-earth-800/70 rounded-lg p-4 gap-2">
                    <div>
                      <div className="font-semibold text-sand-100">{opt.label}</div>
                      <div className="text-sand-300 text-xs mb-1">{opt.description}</div>
                      <div className="text-sand-200 font-medium">€{opt.perNight}/night <span className='text-sand-400 text-xs'>(€{opt.price} total)</span></div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                      <button
                        className="w-8 h-8 flex items-center justify-center rounded bg-earth-700 text-sand-200 text-lg font-bold disabled:opacity-50"
                        onClick={() => setLodgingQuantities(q => ({ ...q, [opt.key]: Math.max(0, q[opt.key as keyof typeof q] - 1) }))}
                        disabled={lodgingQuantities[opt.key as keyof typeof lodgingQuantities] === 0}
                        aria-label={`Remove one ${opt.label}`}
                      >
                        -
                      </button>
                      <span className="w-6 text-center text-sand-100 font-semibold">{lodgingQuantities[opt.key as keyof typeof lodgingQuantities]}</span>
                      <button
                        className="w-8 h-8 flex items-center justify-center rounded bg-sand-200 text-earth-900 text-lg font-bold"
                        onClick={() => setLodgingQuantities(q => ({ ...q, [opt.key]: q[opt.key as keyof typeof q] + 1 }))}
                        aria-label={`Add one ${opt.label}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-6 border-t border-earth-700 pt-4">
                <span className="font-semibold text-sand-200">Lodging Subtotal</span>
                <span className="text-lg font-bold text-sand-100">€{lodgingSubtotal}</span>
              </div>
            </div>
          )}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-display mb-6">Review & Checkout</h2>
              <div className="space-y-4">
                {/* Tickets Summary */}
                {TICKET_OPTIONS.filter(opt => ticketQuantities[opt.key as keyof typeof ticketQuantities] > 0).length > 0 && (
                  <div>
                    <div className="font-semibold text-sand-200 mb-2">Tickets</div>
                    <ul className="space-y-2">
                      {TICKET_OPTIONS.map(opt => (
                        ticketQuantities[opt.key as keyof typeof ticketQuantities] > 0 && (
                          <li key={opt.key} className="flex justify-between items-center text-sand-100">
                            <span>{opt.label} x {ticketQuantities[opt.key as keyof typeof ticketQuantities]}</span>
                            <span>€{ticketQuantities[opt.key as keyof typeof ticketQuantities] * opt.price}</span>
                          </li>
                        )
                      ))}
                    </ul>
                    <div className="flex justify-between items-center mt-2 text-sand-300 text-sm">
                      <span>Tickets Subtotal</span>
                      <span>€{ticketSubtotal}</span>
                    </div>
                  </div>
                )}
                {/* Lodging Summary */}
                {LODGING_OPTIONS.filter(opt => lodgingQuantities[opt.key as keyof typeof lodgingQuantities] > 0).length > 0 && (
                  <div>
                    <div className="font-semibold text-sand-200 mb-2 mt-4">Lodging</div>
                    <ul className="space-y-2">
                      {LODGING_OPTIONS.map(opt => (
                        lodgingQuantities[opt.key as keyof typeof lodgingQuantities] > 0 && (
                          <li key={opt.key} className="flex justify-between items-center text-sand-100">
                            <span>{opt.label} x {lodgingQuantities[opt.key as keyof typeof lodgingQuantities]}</span>
                            <span>€{lodgingQuantities[opt.key as keyof typeof lodgingQuantities] * opt.price}</span>
                          </li>
                        )
                      ))}
                    </ul>
                    <div className="flex justify-between items-center mt-2 text-sand-300 text-sm">
                      <span>Lodging Subtotal</span>
                      <span>€{lodgingSubtotal}</span>
                    </div>
                  </div>
                )}
                {/* Grand Total */}
                <div className="flex justify-between items-center mt-6 border-t border-earth-700 pt-4">
                  <span className="font-bold text-sand-100 text-lg">Total</span>
                  <span className="font-bold text-sand-100 text-lg">€{grandTotal}</span>
                </div>
                {/* Processing Fee */}
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sand-300 text-sm">Payment Processing Fees (6%)</span>
                  <span className="text-sand-300 text-sm">€{processingFee}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-bold text-sand-100 text-lg">Total with Fees</span>
                  <span className="font-bold text-sand-100 text-lg">€{totalWithFee}</span>
                </div>
                <div className="text-sand-400 text-xs mt-1">
                  Assumes payment by credit card. Lower fee option available via direct invoice; DM <a href="mailto:info@letsrendezvous.co" className="underline">info@letsrendezvous.co</a> for details
                </div>
              </div>
            </div>
          )}
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 gap-4">
            <button
              className="px-5 py-2 rounded bg-earth-800 text-sand-200 font-medium disabled:opacity-50 shadow-sm"
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
              disabled={currentStep === 0}
            >
              Back
            </button>
            <button
              className="px-5 py-2 rounded bg-sand-200 text-earth-900 font-semibold disabled:opacity-50 shadow-sm"
              onClick={currentStep === steps.length - 1 ? handleCheckout : () => setCurrentStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={currentStep === steps.length - 1 && (!canCheckout || isLoading)}
            >
              {isLoading && currentStep === steps.length - 1 ? 'Processing...' : (currentStep === steps.length - 1 ? 'Checkout' : 'Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SunscapesCheckoutModal; 